import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { VendorGridService } from './vendor-grid.service';
import { MessageService } from 'primeng/api';
import { helperService } from 'src/app/utils/helper';

@Component({
    selector: 'app-vendor-grid',
    templateUrl: './vendor-grid.component.html',
    styleUrls: ['./vendor-grid.component.scss'],
    standalone: false
})
export class VendorGridComponent implements OnInit {
  vendorAccounts: any[] = [];
  cols: any[] = [];
  vendorDialog = false;
  viewOnly = false;
  title = '';
  loading = false;
  totalRecords = 0;
  first = 1;
  submitted = false;
  btnLoading = false;
  lastTableLazyLoadEvent: any = null;
  vendorLoadingSubscription: any = null;
  selectedVendor: any = null;
  vendorForm: UntypedFormGroup;
  defaultVendorForm: any = {};

  constructor(
    private vendorService: VendorGridService,
    private readonly messageService: MessageService,
    private filterService: helperService,
    private cdr: ChangeDetectorRef
  ) {
    this.vendorForm = new UntypedFormGroup({
      id: new UntypedFormControl(''),
      name: new UntypedFormControl('', [Validators.required]),
      address1: new UntypedFormControl(''),
      address2: new UntypedFormControl(''),
      address3: new UntypedFormControl(''),
      address4: new UntypedFormControl(''),
      address5: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      email: new UntypedFormControl(''),
      isActive: new UntypedFormControl(''),
      qbDateCreated: new UntypedFormControl(''),
      qbVendorId: new UntypedFormControl(''),
    });
  }

  ngOnInit(): void {
    this.initTableCols();
    this.defaultVendorForm = this.vendorForm.value;
    this.loading = true;
    this.loadVendorAccounts({ first: 1, rows: 10 });
  }

  private initTableCols() {
    this.cols = [
      { field: 'name', header: 'Vendor Name' ,isSortable:true,isFilterable:true},
      { field: 'address1', header: 'Address 1' ,isSortable:true,isFilterable:true},
      // { field: 'address2', header: 'Address 2' ,isSortable:true,isFilterable:true},
      // { field: 'address3', header: 'Address 3' ,isSortable:true,isFilterable:true},
      // { field: 'address4', header: 'Address 4' ,isSortable:true,isFilterable:true},
      // { field: 'address5', header: 'Address 5' ,isSortable:true,isFilterable:true},
      { field: 'phone', header: 'Phone' ,isSortable:true,isFilterable:true},
      { field: 'email', header: 'Email',isSortable:true,isFilterable:true },
      { field: 'isActive', header: 'Status' ,isSortable:true,isFilterable:true},
      { field: 'qbDateCreated', header: 'QB Date Created' ,isSortable:false,isFilterable:false},
      { field: 'qbVendorId', header: 'QB Vendor ID' ,isSortable:true,isFilterable:true},
      { field: 'id', header: 'ID' ,isSortable:true,isFilterable:true}
    ];
  }

  loadVendorAccounts(event: any) {
    event = event ? event : this.lastTableLazyLoadEvent || { first: 1, rows: 10 };
    this.lastTableLazyLoadEvent = event;
    this.loading = true;
    event.first = event.first || 1;
    event.rows = event.rows || 10;
    let payload = this.filterService.generateEventPayload(event);
    if(this.vendorLoadingSubscription)
      this.vendorLoadingSubscription.unsubscribe();
    this.vendorLoadingSubscription=this.vendorService.getVendorList(Math.floor(event.first / event.rows + 1), event.rows, payload)
      .subscribe(res => {
        this.vendorAccounts = res?.data||[];
        this.totalRecords = res?.customValues?.totalNumberOfRecords || this.vendorAccounts.length;
        this.loading = false;
      }, err => {
        this.vendorAccounts = [];
        this.totalRecords = 0;
        this.loading = false;
      });
  }

  viewVendor(vendor: any) {
    this.vendorForm.patchValue(vendor);
    this.vendorForm.disable();
    this.vendorDialog = true;
    this.viewOnly = true;
    this.title = 'View Vendor';
  }

  editVendor(vendor: any) {
    this.vendorForm.patchValue(vendor);
    this.vendorForm.enable();
    // Disable id, qbDateCreated, qbVendorId fields
    this.vendorForm.get('id')?.disable();
    this.vendorForm.get('qbDateCreated')?.disable();
    this.vendorForm.get('qbVendorId')?.disable();
    this.vendorDialog = true;
    this.viewOnly = false;
    this.title = 'Edit Vendor';
  }

  saveVendor() {
    if (this.btnLoading) return;
    this.submitted = true;
    this.messageService.clear();
    if (this.vendorForm.valid) {
      this.btnLoading = true;
      this.vendorForm.disable();
      const payload = this.vendorForm.getRawValue();
      // Remove id for new vendor if needed (here, only update is supported)
      // Call update API
      this.vendorService.saveVendor(payload, payload.id).subscribe(
        res => {
          this.btnLoading = false;
          if (res.message) {
            this.messageService.add({ severity: 'success', detail: 'Vendor updated successfully' });
            this.vendorDialog = false;
            this.loadVendorAccounts(this.lastTableLazyLoadEvent);
          } else {
            this.messageService.add({ severity: 'error', detail: res.message });
          }
          this.vendorForm.enable();
        },
        error => {
          this.btnLoading = false;
          this.messageService.add({ severity: 'error', detail: error.message });
          this.vendorForm.enable();
        }
      );
    } else {
      const invalidControls = Object.keys(this.vendorForm.controls)
        .filter(key => this.vendorForm.controls[key].invalid)
        .map(key => key);
      this.messageService.add({
        severity: 'error',
        detail: `Please fill all mandatory fields correctly. Invalid: ${invalidControls.join(', ')}`
      });
    }
  }

  closeDialog() {
    this.vendorDialog = false;
    this.vendorForm.reset(this.defaultVendorForm);
    this.vendorForm.enable();
    this.selectedVendor = null;
    this.viewOnly = false;
    this.title = '';
    this.cdr.detectChanges();
  }

  // Form control getters for template usage
  get name() { return this.vendorForm.get('name'); }
  get address1() { return this.vendorForm.get('address1'); }
  get address2() { return this.vendorForm.get('address2'); }
  get address3() { return this.vendorForm.get('address3'); }
  get address4() { return this.vendorForm.get('address4'); }
  get address5() { return this.vendorForm.get('address5'); }
  get phone() { return this.vendorForm.get('phone'); }
  get email() { return this.vendorForm.get('email'); }
  get isActive() { return this.vendorForm.get('isActive'); }
  get qbDateCreated() { return this.vendorForm.get('qbDateCreated'); }
  get qbVendorId() { return this.vendorForm.get('qbVendorId'); }
  get id() { return this.vendorForm.get('id'); }
}
